import { KVTwoPointFiveDAnnotation } from './KVTwoPointFiveDAnnotation'
import {
  KVAerospaceAnnotation,
  KVG1N6Annotation,
  KVNavyAnnotation,
  KVNavyMultiLayerAnnotation,
  KVRooftopAnnotation,
  WorldPosition,
} from 'types'
import { NDEData } from 'types/kestrelViewer/KVPrimitive'

import {
  isPhotoAnnotationProjection,
  type KVPhotoAnnotationProjection,
} from './KVPhotoAnnotationProjection'

export interface KVTagMetadata {
  value: string
  derivedTag?: boolean
}

export class KVPointGeometry {
  kind = 'point' as const
  position?: WorldPosition
  normal?: WorldPosition

  constructor(position?: WorldPosition, normal?: WorldPosition) {
    this.position = position
    this.normal = normal
  }

  toJSON() {
    return {
      kind: this.kind,
      position: this.position,
      normal: this.normal,
    }
  }
}

export const FILL_MODE = {
  PLANAR: 'planar',
  CONFORMING: 'conforming',
  DEPTH: 'depth',
} as const

export type SurfaceAreaFillMode = (typeof FILL_MODE)[keyof typeof FILL_MODE]
export type KVMeasurementSource = 'auto' | 'manual'

export interface KVDepthProbe {
  planeAnchorU: number
  planeAnchorV: number
}

export class KVPolygonGeometry {
  kind = 'polygon' as const
  points: WorldPosition[]
  normal?: WorldPosition
  // Per-vertex normals (same length as points when present); used for conforming mesh determinism
  vertexNormals?: WorldPosition[]
  // Defaults to FILL_MODE.PLANAR if not specified (for backward compatibility with rolodex entries)
  fillMode: SurfaceAreaFillMode
  depthProbe?: KVDepthProbe

  constructor(
    points: WorldPosition[],
    normal?: WorldPosition,
    fillMode: SurfaceAreaFillMode = FILL_MODE.PLANAR,
    vertexNormals?: WorldPosition[],
    depthProbe?: KVDepthProbe
  ) {
    this.points = points
    this.normal = normal
    this.fillMode = fillMode
    this.vertexNormals = vertexNormals
    this.depthProbe = depthProbe
  }

  toJSON() {
    return {
      kind: this.kind,
      points: this.points,
      normal: this.normal,
      vertexNormals: this.vertexNormals,
      fillMode: this.fillMode,
      depthProbe: this.depthProbe,
    }
  }
}

export class KVImageBoundingBoxGeometry {
  kind = 'imageBoundingBox' as const
  photoId: string
  x: number
  y: number
  width: number
  height: number
  projection?: KVPhotoAnnotationProjection

  constructor(
    photoId: string,
    x: number,
    y: number,
    width: number,
    height: number,
    projection?: KVPhotoAnnotationProjection
  ) {
    this.photoId = photoId
    this.x = x
    this.y = y
    this.width = width
    this.height = height
    this.projection = projection
  }

  toJSON() {
    return {
      kind: this.kind,
      photoId: this.photoId,
      x: this.x,
      y: this.y,
      width: this.width,
      height: this.height,
    }
  }

  static fromJSON(json: unknown): KVImageBoundingBoxGeometry | undefined {
    if (!json || typeof json !== 'object') return undefined
    const geometry = json as Record<string, unknown>
    if (geometry.kind !== 'imageBoundingBox') return undefined
    if (typeof geometry.photoId !== 'string' || geometry.photoId === '') {
      return undefined
    }
    const x = coerceFiniteNumber(geometry.x)
    const y = coerceFiniteNumber(geometry.y)
    const width = coerceFiniteNumber(geometry.width)
    const height = coerceFiniteNumber(geometry.height)
    if (
      x === undefined ||
      y === undefined ||
      width === undefined ||
      height === undefined ||
      width <= 0 ||
      height <= 0
    ) {
      return undefined
    }
    return new KVImageBoundingBoxGeometry(
      geometry.photoId,
      x,
      y,
      width,
      height,
      isPhotoAnnotationProjection(geometry.projection)
        ? geometry.projection
        : undefined
    )
  }
}

export type KVGeometry =
  | KVPointGeometry
  | KVPolygonGeometry
  | KVImageBoundingBoxGeometry

export interface KVCaptureViewRotation {
  pitch: number
  yaw: number
  roll: number
}

export interface KVCreationCaptureView {
  location: WorldPosition
  rotation: KVCaptureViewRotation
  fov: number
}

function coerceFiniteNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string') {
    const n = parseFloat(value)
    return Number.isFinite(n) ? n : undefined
  }
  return undefined
}

function coerceMeasurementSource(
  value: unknown
): KVMeasurementSource | undefined {
  return value === 'auto' || value === 'manual' ? value : undefined
}

export class KVAnnotation {
  id: string
  label: string
  notes: string
  severity: string
  type: string
  visible: boolean
  imageNames: string[]
  userName: string
  userEmail: string
  createdAt: string
  historical: boolean // Historical field is only set locally since it changes based on which model pulls the annotation
  mode?: 'photo'
  twoPointFiveDAnnotation?: KVTwoPointFiveDAnnotation

  centeredNormal?: WorldPosition
  centeredPosition?: WorldPosition

  geometry?: KVGeometry

  isSegmentationSurfaceArea?: boolean

  area?: number
  perimeter?: number

  // Raw Unreal-unit area/perimeter
  areaUU?: number
  perimeterUU?: number

  depth?: number
  depthSource?: KVMeasurementSource
  baseMetalThickness?: number

  ndeData?: NDEData

  boundingBoxLength?: number
  boundingBoxWidth?: number

  navyAnnotation?: KVNavyAnnotation
  navyMultiLayerAnnotation?: KVNavyMultiLayerAnnotation
  rooftopAnnotation?: KVRooftopAnnotation
  g1n6Annotation?: KVG1N6Annotation
  aerospaceAnnotation?: KVAerospaceAnnotation

  // For dataset generation; did a human OK this annotation?
  humanValidated?: boolean

  group?: string
  autoInspect?: boolean

  creationRayDirection?: WorldPosition // Ray from camera to annotation at creation
  cameraFacingNormal?: WorldPosition // Normal facing toward camera at creation
  creationCaptureView?: KVCreationCaptureView
  inspectionArea: string | null

  tags: Record<string, KVTagMetadata>

  static creationCaptureViewFromJSON(
    value: unknown
  ): KVCreationCaptureView | undefined {
    if (!value || typeof value !== 'object') return undefined
    const v = value as Record<string, unknown>
    const loc = v.location as Record<string, unknown> | undefined
    const rot = v.rotation as Record<string, unknown> | undefined
    const x = loc ? coerceFiniteNumber(loc.x) : undefined
    const y = loc ? coerceFiniteNumber(loc.y) : undefined
    const z = loc ? coerceFiniteNumber(loc.z) : undefined
    const pitch = rot ? coerceFiniteNumber(rot.pitch) : undefined
    const yaw = rot ? coerceFiniteNumber(rot.yaw) : undefined
    const roll = rot ? coerceFiniteNumber(rot.roll) : undefined
    const fov = coerceFiniteNumber(v.fov)
    if (
      x === undefined ||
      y === undefined ||
      z === undefined ||
      pitch === undefined ||
      yaw === undefined ||
      roll === undefined ||
      fov === undefined
    ) {
      return undefined
    }
    return {
      location: { x, y, z },
      rotation: { pitch, yaw, roll },
      fov,
    }
  }

  constructor(id: string) {
    this.id = id
    this.label = ''
    this.notes = ''
    this.severity = '3'
    this.type = 'Misc.'
    this.visible = true
    this.imageNames = []
    this.userName = ''
    this.userEmail = ''
    this.createdAt = new Date().toISOString()
    this.historical = false
    this.inspectionArea = null
    this.tags = {}
  }

  /* eslint-disable @typescript-eslint/no-explicit-any */
  static fromJSON(json: any): KVAnnotation {
    const annotation = new KVAnnotation(json.annotationId)
    if (json.annotationLabel) annotation.label = json.annotationLabel
    if (json.annotationNotes) annotation.notes = json.annotationNotes
    if (json.annotationSeverity || json.annotationSeverity === '')
      annotation.severity = json.annotationSeverity
    if (json.annotationType) annotation.type = json.annotationType
    if (json.mode === 'photo') annotation.mode = 'photo'
    if (json.annotationVisible) annotation.visible = json.annotationVisible
    if (json.annotationImageNames)
      annotation.imageNames = json.annotationImageNames
    if (json.navyAnnotation)
      annotation.navyAnnotation = KVNavyAnnotation.fromJSON(json.navyAnnotation)
    if (json.navyMultiLayerAnnotation)
      annotation.navyMultiLayerAnnotation = KVNavyMultiLayerAnnotation.fromJSON(
        json.navyMultiLayerAnnotation
      )
    if (json.rooftopAnnotation)
      annotation.rooftopAnnotation = KVRooftopAnnotation.fromJSON(
        json.rooftopAnnotation
      )
    if (json.g1n6Annotation)
      annotation.g1n6Annotation = KVG1N6Annotation.fromJSON(json.g1n6Annotation)
    if (json.aerospaceAnnotation)
      annotation.aerospaceAnnotation = KVAerospaceAnnotation.fromJSON(
        json.aerospaceAnnotation
      )
    if (json.userName) annotation.userName = json.userName
    if (json.userEmail) annotation.userEmail = json.userEmail
    if (json.createdAt) annotation.createdAt = json.createdAt
    if (json.normal) {
      annotation.centeredNormal = {
        x: json.normal.x,
        y: json.normal.y,
        z: json.normal.z,
      }
    } else if (json.centeredNormal) {
      annotation.centeredNormal = {
        x: json.centeredNormal.x,
        y: json.centeredNormal.y,
        z: json.centeredNormal.z,
      }
    }

    if (json.centeredPosition) {
      annotation.centeredPosition = {
        x: json.centeredPosition.x,
        y: json.centeredPosition.y,
        z: json.centeredPosition.z,
      }
    }

    if (json.isSegmentationSurfaceArea !== undefined) {
      annotation.isSegmentationSurfaceArea = json.isSegmentationSurfaceArea
    }

    if (json.tags) {
      const tags: Record<string, KVTagMetadata> = {}
      for (const [key, value] of Object.entries(json.tags)) {
        tags[key] = value as KVTagMetadata
      }
      annotation.tags = tags
    }

    if (json.group) {
      annotation.group = json.group
    }

    if (json.humanValidated !== undefined) {
      annotation.humanValidated = json.humanValidated
    }
    if (json.autoInspect !== undefined) {
      annotation.autoInspect = Boolean(json.autoInspect)
    }

    annotation.area = json.area ? parseFloat(json.area as string) : undefined
    annotation.perimeter = json.perimeter
      ? parseFloat(json.perimeter as string)
      : undefined
    annotation.areaUU = json.areaUU
      ? parseFloat(json.areaUU as string)
      : undefined
    annotation.perimeterUU = json.perimeterUU
      ? parseFloat(json.perimeterUU as string)
      : undefined
    annotation.depth =
      json.depth !== undefined && json.depth !== null
        ? parseFloat(json.depth as string)
        : undefined
    annotation.depthSource = coerceMeasurementSource(json.depthSource)
    annotation.baseMetalThickness =
      json.baseMetalThickness !== undefined && json.baseMetalThickness !== null
        ? parseFloat(json.baseMetalThickness as string)
        : undefined
    if (json.ndeData) {
      annotation.ndeData = json.ndeData as NDEData
    }

    if (json.boundingBoxLength !== undefined) {
      annotation.boundingBoxLength = parseFloat(
        json.boundingBoxLength as string
      )
    }
    if (json.boundingBoxWidth !== undefined) {
      annotation.boundingBoxWidth = parseFloat(json.boundingBoxWidth as string)
    }

    if (json.geometry) {
      if (json.geometry.kind === 'point') {
        annotation.geometry = new KVPointGeometry(
          json.geometry.position,
          json.geometry.normal
        )
      } else if (json.geometry.kind === 'imageBoundingBox') {
        const imageBox = KVImageBoundingBoxGeometry.fromJSON(json.geometry)
        if (imageBox) annotation.geometry = imageBox
      } else if (json.geometry.kind === 'polygon') {
        // Parse fillMode from geometry or fall back to annotation-level fillMode for backward compatibility
        const fillMode: SurfaceAreaFillMode =
          json.geometry.fillMode ?? json.fillMode ?? FILL_MODE.PLANAR
        const vertexNormals = Array.isArray(json.geometry.vertexNormals)
          ? (json.geometry.vertexNormals as WorldPosition[])
          : undefined
        const rawDepthProbe = json.geometry.depthProbe ?? json.depthProbe
        const depthProbe =
          rawDepthProbe &&
          typeof rawDepthProbe.planeAnchorU === 'number' &&
          typeof rawDepthProbe.planeAnchorV === 'number'
            ? {
                planeAnchorU: rawDepthProbe.planeAnchorU,
                planeAnchorV: rawDepthProbe.planeAnchorV,
              }
            : undefined
        annotation.geometry = new KVPolygonGeometry(
          json.geometry.points,
          json.geometry.normal,
          fillMode,
          vertexNormals,
          depthProbe
        )
      }
    }

    if (json.creationRayDirection)
      annotation.creationRayDirection = json.creationRayDirection
    if (json.cameraFacingNormal)
      annotation.cameraFacingNormal = json.cameraFacingNormal
    const creationCaptureView = KVAnnotation.creationCaptureViewFromJSON(
      json.creationCaptureView
    )
    if (creationCaptureView) {
      annotation.creationCaptureView = creationCaptureView
    }
    annotation.inspectionArea = (json.inspectionArea as string | null) ?? null

    if (json.twoPointFiveDAnnotation)
      annotation.twoPointFiveDAnnotation = json.twoPointFiveDAnnotation

    return annotation
  }

  static toJSON(annotation: KVAnnotation): any {
    const json: any = {
      annotationId: annotation.id,
      annotationLabel: annotation.label,
      annotationNotes: annotation.notes,
      annotationSeverity: annotation.severity,
      annotationType: annotation.type,
      annotationVisible: annotation.visible,
      annotationImageNames: annotation.imageNames,
      userName: annotation.userName,
      userEmail: annotation.userEmail,
      createdAt: annotation.createdAt,
      centeredNormal: annotation.centeredNormal,
      centeredPosition: annotation.centeredPosition,
      isSegmentationSurfaceArea: annotation.isSegmentationSurfaceArea ?? false,
      area: annotation.area,
      perimeter: annotation.perimeter,
      areaUU: annotation.areaUU,
      perimeterUU: annotation.perimeterUU,
      depth: annotation.depth,
      depthSource: annotation.depthSource,
      baseMetalThickness: annotation.baseMetalThickness,
      ndeData: annotation.ndeData,
      boundingBoxLength: annotation.boundingBoxLength,
      boundingBoxWidth: annotation.boundingBoxWidth,
      tags: annotation.tags,
      group: annotation.group,
      autoInspect: annotation.autoInspect,
      humanValidated: annotation.humanValidated,
      creationRayDirection: annotation.creationRayDirection,
      cameraFacingNormal: annotation.cameraFacingNormal,
      creationCaptureView: annotation.creationCaptureView,
      inspectionArea: annotation.inspectionArea,
    }
    if (annotation.twoPointFiveDAnnotation)
      json.twoPointFiveDAnnotation = annotation.twoPointFiveDAnnotation
    if (annotation.mode === 'photo') {
      json.mode = 'photo'
    }
    if (annotation.navyAnnotation) {
      json.navyAnnotation = KVNavyAnnotation.toJSON(annotation.navyAnnotation)
    }
    if (annotation.navyMultiLayerAnnotation) {
      json.navyMultiLayerAnnotation = KVNavyMultiLayerAnnotation.toJSON(
        annotation.navyMultiLayerAnnotation
      )
    }
    if (annotation.rooftopAnnotation) {
      json.rooftopAnnotation = KVRooftopAnnotation.toJSON(
        annotation.rooftopAnnotation
      )
    }
    if (annotation.g1n6Annotation) {
      json.g1n6Annotation = KVG1N6Annotation.toJSON(annotation.g1n6Annotation)
    }
    if (annotation.aerospaceAnnotation) {
      json.aerospaceAnnotation = KVAerospaceAnnotation.toJSON(
        annotation.aerospaceAnnotation
      )
    }

    if (annotation.geometry) {
      json.geometry = annotation.geometry.toJSON()
    }
    return json
  }

  static toKestrelJSON(annotation: KVAnnotation): any {
    // Use direct inspectionArea field, fall back to tag for backward compatibility
    const inspectionAreaId =
      annotation.inspectionArea ??
      annotation.tags?.inspectionArea?.value ??
      null

    if (annotation.geometry?.kind === 'point') {
      return {
        annotationId: annotation.id,
        annotationLabel: annotation.label,
        annotationSeverity: annotation.severity,
        annotationVisible: annotation.visible,
        annotationPosition: annotation.geometry.position,
        annotationNormal: annotation.geometry.normal,
        annotationHistorical: annotation.historical,
        inspectionArea: inspectionAreaId,
        creationRayDirection: annotation.creationRayDirection,
      }
    } else if (annotation.geometry?.kind === 'polygon') {
      return {
        surfaceAreaId: annotation.id,
        surfaceAreaLabel: annotation.label,
        surfaceAreaVisible: annotation.visible,
        surfaceAreaPoints: annotation.geometry.points,
        vertexNormals: annotation.geometry.vertexNormals,
        surfaceAreaSeverity: annotation.severity,
        isSegmentationSurfaceArea:
          annotation.isSegmentationSurfaceArea ?? false,
        autoInspect: annotation.autoInspect ?? false,
        fillMode: annotation.geometry.fillMode,
        area: annotation.area?.toString() || '',
        perimeter: annotation.perimeter?.toString() || '',
        areaUU: annotation.areaUU?.toString() || '',
        perimeterUU: annotation.perimeterUU?.toString() || '',
        depth: annotation.depth?.toString() || '',
        baseMetalThickness:
          annotation.baseMetalThickness === undefined
            ? ''
            : annotation.baseMetalThickness.toString(),
        depthSource: annotation.depthSource || '',
        ndeData: annotation.ndeData,
        boundingBoxLength: annotation.boundingBoxLength?.toString() || '',
        boundingBoxWidth: annotation.boundingBoxWidth?.toString() || '',
        surfaceAreaHistorical: annotation.historical,
        inspectionArea: inspectionAreaId,
        creationRayDirection: annotation.creationRayDirection,
        cameraFacingNormal: annotation.cameraFacingNormal,
        creationCaptureView: annotation.creationCaptureView,
        depthProbe: annotation.geometry.depthProbe,
      }
    }
  }
  static kestrelPointAnnotations(annotations: KVAnnotation[]): any[] {
    return annotations
      .filter((annotation) => annotation.geometry?.kind === 'point')
      .map((annotation) => KVAnnotation.toKestrelJSON(annotation))
  }
  static kestrelPolygonAnnotations(annotations: KVAnnotation[]): any[] {
    return annotations
      .filter((annotation) => annotation.geometry?.kind === 'polygon')
      .map((annotation) => KVAnnotation.toKestrelJSON(annotation))
  }
}
